import React, { useState, useEffect } from 'react';
import { auth, googleProvider, db } from '../firebase';
import { Logo } from './icons/Logo';
import { GoogleIcon, ArrowLeftIcon } from './icons/InterfaceIcons';
import { Helmet } from 'react-helmet-async';

const ProgressStepper = ({ currentStep, totalSteps }) => {
    return (
        <div className="flex justify-center space-x-2 mb-8">
            {Array.from({ length: totalSteps }).map((_, index) => (
                <div
                    key={index}
                    className={`h-2 rounded-full transition-all duration-300 ${
                        index < currentStep ? 'bg-brand-primary w-6' : 'bg-brand-border w-2'
                    }`}
                />
            ))}
        </div>
    );
};

// FIX: Add explicit types for props to allow 'key' prop in map.
const OnboardingButton: React.FC<{ onClick: (e?: any) => void; children: React.ReactNode; className?: string; disabled?: boolean; }> = ({ onClick, children, className = '', disabled=false }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`w-full text-left p-4 border-2 rounded-lg text-lg font-semibold transition-all duration-200 ${className} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
        {children}
    </button>
);


export const SignUp = ({ onNavigateToSignIn }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    exam: '',
    otherExam: '',
    class: '',
    targetYear: '',
    phone: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const TOTAL_STEPS = 6;

  const handleNext = () => setStep(prev => prev + 1);
  const handleBack = () => setStep(prev => prev - 1);
  
  const handleSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // For 'Other' exam, we don't auto-advance. User needs to type and click next.
    if (field === 'exam' && value === 'Other') {
        return;
    }
    handleNext();
  };
  
  const handleFinalSubmit = async (authProvider) => {
    setError('');
    setLoading(true);

    try {
        let firebaseUser;
        let nameForDb;

        if (authProvider === 'email') {
            if (formData.password.length < 6) throw new Error("Password should be at least 6 characters.");
            const userCredential = await auth.createUserWithEmailAndPassword(formData.email, formData.password);
            if (userCredential.user) {
              await userCredential.user.updateProfile({ displayName: formData.name });
            }
            firebaseUser = userCredential.user;
            nameForDb = formData.name;
        } else {
            const result = await auth.signInWithPopup(googleProvider);
            firebaseUser = result.user;
            // Use Google's display name, but fall back to form data if it exists (e.g., they filled name then chose Google)
            nameForDb = firebaseUser.displayName || formData.name; 
        }

        const userRef = db.ref(`users/${firebaseUser.uid}`);
        const userSnapshot = await userRef.get();
        if (!userSnapshot.exists()) {
             const userData = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                name: nameForDb,
                fullName: nameForDb,
                exam: formData.exam === 'Other' ? formData.otherExam : formData.exam,
                class: formData.class,
                targetYear: formData.targetYear,
                phone: formData.phone,
                createdAt: new Date().toISOString(),
             };
             await userRef.set(userData);
        }
        // onAuthStateChanged in App.tsx will navigate to dashboard
    } catch (err) {
        if (err.code === 'auth/email-already-in-use') {
            setError('This email is already registered. Please sign in.');
        } else {
            setError(err.message || 'An error occurred. Please try again.');
        }
    } finally {
        setLoading(false);
    }
  }


  const renderStep = () => {
    switch (step) {
      case 1: // Welcome
        return (
          <>
            <h1 className="text-4xl font-bold text-center">👋 Welcome!</h1>
            <p className="text-brand-text-secondary text-lg text-center mt-2">Let’s get you set up for your exam prep journey.</p>
            <button onClick={handleNext} className="w-full mt-8 bg-brand-primary text-white font-bold py-4 px-4 rounded-lg text-lg hover:bg-brand-primary-hover transition-colors">
              Get Started
            </button>
          </>
        );
      case 2: // Name
        return (
          <>
            <h2 className="text-3xl font-bold">What should we call you?</h2>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Your full name"
              className="w-full mt-4 p-4 border-2 border-brand-border rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary bg-brand-surface text-brand-text-primary"
              autoFocus
            />
            <button onClick={handleNext} disabled={!formData.name} className="w-full mt-4 bg-brand-primary text-white font-bold py-4 px-4 rounded-lg text-lg hover:bg-brand-primary-hover transition-colors disabled:bg-gray-300">
              Next
            </button>
          </>
        );
      case 3: // Exam Selection
        return (
          <>
            <h2 className="text-3xl font-bold">Which exam are you preparing for?</h2>
            <div className="space-y-3 mt-4">
                {['JEE', 'NEET', 'UPSC', 'SSC', 'NDA', 'Other'].map(exam => (
                    <OnboardingButton key={exam} onClick={() => handleSelect('exam', exam)} className={formData.exam === exam ? "border-brand-primary bg-brand-primary/10" : "border-brand-border hover:border-gray-400"}>
                        {exam}
                    </OnboardingButton>
                ))}
            </div>
            {formData.exam === 'Other' && (
                <div className="mt-4 animate-fade-in">
                     <input
                        type="text"
                        value={formData.otherExam}
                        onChange={(e) => setFormData({...formData, otherExam: e.target.value})}
                        placeholder="Please specify your exam"
                        className="w-full p-4 border-2 border-brand-border rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary bg-brand-surface text-brand-text-primary"
                        autoFocus
                    />
                     <button onClick={handleNext} disabled={!formData.otherExam} className="w-full mt-4 bg-brand-primary text-white font-bold py-4 px-4 rounded-lg text-lg hover:bg-brand-primary-hover transition-colors disabled:bg-gray-300">
                        Next
                    </button>
                </div>
            )}
          </>
        );
       case 4: // Class/Year
        return (
          <>
            <h2 className="text-3xl font-bold">Which class are you in?</h2>
            <div className="space-y-3 mt-4">
                {['11th', '12th', 'Drop Year', 'Graduate'].map(c => (
                     <OnboardingButton key={c} onClick={() => handleSelect('class', c)} className={formData.class === c ? "border-brand-primary bg-brand-primary/10" : "border-brand-border hover:border-gray-400"}>
                        {c}
                    </OnboardingButton>
                ))}
            </div>
          </>
        );
        case 5: // Target Year
            const currentYear = new Date().getFullYear();
            const years = [currentYear, currentYear + 1, currentYear + 2, currentYear + 3];
            return (
              <>
                <h2 className="text-3xl font-bold">When's your big exam?</h2>
                 <div className="space-y-3 mt-4">
                    {years.map(y => (
                        <OnboardingButton key={y} onClick={() => handleSelect('targetYear', y.toString())} className={formData.targetYear === y.toString() ? "border-brand-primary bg-brand-primary/10" : "border-brand-border hover:border-gray-400"}>
                            {y}
                        </OnboardingButton>
                    ))}
                </div>
              </>
            );
        case 6: // Contact and Account
            return (
                 <>
                    <h2 className="text-3xl font-bold">Let's create your account</h2>
                    <p className="text-brand-text-secondary mt-1">Just a few more details to get you started.</p>
                    <div className="space-y-4 mt-6">
                        <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="Phone number (Optional)" className="w-full p-4 border-2 border-brand-border rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary bg-brand-surface text-brand-text-primary" />
                        <hr className="border-brand-border" />
                        <button type="button" onClick={() => handleFinalSubmit('google')} disabled={loading} className="w-full flex justify-center items-center py-3 px-4 border border-brand-border rounded-md shadow-sm text-sm font-medium text-brand-text-primary bg-white hover:bg-gray-50 disabled:opacity-50">
                            <GoogleIcon className="w-5 h-5 mr-3" /> Continue with Google
                        </button>
                        <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-brand-border" /></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-brand-surface text-brand-text-secondary">OR</span></div></div>
                        <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="Email address" className="w-full p-4 border-2 border-brand-border rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary bg-brand-surface text-brand-text-primary" />
                        <input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="Create a password (6+ characters)" className="w-full p-4 border-2 border-brand-border rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary bg-brand-surface text-brand-text-primary" />
                        
                        {error && <p className="text-sm text-brand-accent-red text-center">{error}</p>}
                        
                        <button onClick={() => handleFinalSubmit('email')} disabled={loading || !formData.email || !formData.password} className="w-full bg-brand-primary text-white font-bold py-4 px-4 rounded-lg text-lg hover:bg-brand-primary-hover transition-colors disabled:bg-gray-300">
                            {loading ? 'Creating Account...' : 'Create Account & Begin!'}
                        </button>
                    </div>
                 </>
            )
      default:
        return null;
    }
  };

  return (
    <main className="flex-1 flex flex-col items-center justify-center p-4 bg-brand-bg">
        <Helmet>
          <title>Create Account - TestSeries</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="w-full max-w-lg mx-auto">
            {step > 1 && (
                <div className="flex justify-between items-center mb-4">
                    <button onClick={handleBack} className="flex items-center space-x-2 text-sm font-semibold text-brand-text-secondary hover:text-brand-primary">
                        <ArrowLeftIcon className="w-5 h-5" />
                        <span>Back</span>
                    </button>
                    <p className="text-sm font-semibold text-brand-text-secondary">Step {step-1} of {TOTAL_STEPS - 1}</p>
                </div>
            )}
            <div className="bg-brand-surface rounded-2xl shadow-card border border-brand-border p-8 animate-fade-in">
                {step > 1 && <ProgressStepper currentStep={step-1} totalSteps={TOTAL_STEPS - 1} />}
                {renderStep()}
            </div>
             <p className="text-brand-text-secondary mt-8 text-center">
                Already have an account?{' '}
                <button onClick={onNavigateToSignIn} className="font-semibold text-brand-primary hover:text-brand-primary-hover">
                Sign in
                </button>
            </p>
        </div>
    </main>
  );
};